import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { InfoCard } from "@/components/toggles";
import Brand from "@/components/common/Brand";
import api, { formatError } from "@/lib/api";

export default function ResetPasswordPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (pw.length < 8) { setError(t("reset_password.too_short")); return; }
    if (pw !== pw2) { setError(t("reset_password.mismatch")); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      setDone(true);
      setTimeout(() => navigate("/login"), 2500);
    } catch (err) {
      setError(formatError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="reset-password-page">
      <div className="h-14 bg-[#0A0A0A] flex items-center justify-between px-4">
        <button data-testid="reset-back-btn" onClick={() => navigate("/login")} className="text-white/70 hover:text-white transition-colors">
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
        </button>
        <div className="flex items-baseline"><Brand className="text-xl" /></div>
        <div className="w-5" />
      </div>

      <div className="flex-1 flex flex-col justify-center px-6 max-w-md mx-auto w-full">
        <h1 className="text-2xl font-bold text-[#0A0A0A] tracking-tight">{t("reset_password.title")}</h1>
        <p className="text-sm text-[#999] mt-1">{t("reset_password.subtitle")}</p>

        {!token && <div className="mt-4"><InfoCard variant="danger" size="sm" data-testid="reset-no-token">{t("reset_password.no_token")}</InfoCard></div>}
        {error && <div className="mt-4"><InfoCard variant="danger" size="sm">{error}</InfoCard></div>}
        {done && <div className="mt-4"><InfoCard variant="success" size="sm" data-testid="reset-success">{t("reset_password.success")}</InfoCard></div>}

        {!done && token && (
          <form onSubmit={handleSubmit} className="mt-6 flex flex-col gap-4">
            <div>
              <label className="text-xs font-medium text-[#666] uppercase tracking-wider">{t("reset_password.new_password")}</label>
              <input
                data-testid="reset-password-input"
                type="password"
                value={pw}
                onChange={(e) => setPw(e.target.value)}
                className="mt-1 w-full h-12 px-4 rounded-full border border-[#E5E5E5] bg-white text-sm focus:outline-none focus:ring-1 focus:ring-[#0A0A0A]"
                placeholder="••••••••"
                autoComplete="new-password"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-[#666] uppercase tracking-wider">{t("reset_password.confirm_password")}</label>
              <input
                data-testid="reset-password-confirm"
                type="password"
                value={pw2}
                onChange={(e) => setPw2(e.target.value)}
                className="mt-1 w-full h-12 px-4 rounded-full border border-[#E5E5E5] bg-white text-sm focus:outline-none focus:ring-1 focus:ring-[#0A0A0A]"
                placeholder="••••••••"
                autoComplete="new-password"
              />
            </div>
            <button
              data-testid="reset-submit-btn"
              type="submit"
              disabled={loading}
              className="h-12 rounded-full bg-[#0A0A0A] text-white text-sm font-medium disabled:opacity-40">
              {loading ? t("reset_password.saving") : t("reset_password.submit")}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
